import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import React, { useCallback, useMemo } from 'react';

import { ColumnDef, SortModel } from './ServiceTable.types';
import { ServiceTableHead } from './ServiceTableHead';
import { ServiceTableRow } from './ServiceTableRow';

export interface ServiceTableProps {
  data: Record<string, unknown>[];
  columns: ColumnDef[];
  sortModel: SortModel;
  onSortChange: (model: SortModel) => void;
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  onPreview: (id: number) => void;
  onRowAction: (id: number, action: string) => void;
  loading?: boolean;
}

export const ServiceTable: React.FC<ServiceTableProps> = ({
  data,
  columns,
  sortModel,
  onSortChange,
  selectedIds,
  onSelectionChange,
  onPreview,
  onRowAction,
  loading,
}) => {
  const allIds = useMemo(() => data.map((r) => r.serviceId as number).filter(Boolean), [data]);
  const visibleColumns = useMemo(() => columns.filter((c) => c.visible), [columns]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  const someSelected = allIds.some((id) => selectedIds.includes(id));

  const handleSort = useCallback(
    (field: string) => {
      if (sortModel.field !== field) {
        // New column: start with asc
        onSortChange({ field, direction: 'asc' });
      } else if (sortModel.direction === 'asc') {
        // asc → desc
        onSortChange({ field, direction: 'desc' });
      } else {
        // desc → off (revert to default)
        onSortChange({ field: 'serviceId', direction: 'desc' });
      }
    },
    [sortModel, onSortChange],
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      onSelectionChange(checked ? allIds : []);
    },
    [allIds, onSelectionChange],
  );

  const toggleSelection = useCallback(
    (id: number) => {
      const next = selectedIds.includes(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id];
      onSelectionChange(next);
    },
    [selectedIds, onSelectionChange],
  );

  const handleShiftClick = useCallback(
    (index: number) => {
      const lastSelectedIdx = data.findIndex(
        (r) => r.serviceId === selectedIds[selectedIds.length - 1],
      );
      if (lastSelectedIdx === -1) {
        const id = data[index]?.serviceId as number;
        if (id) onSelectionChange([...selectedIds, id]);
        return;
      }
      const start = Math.min(lastSelectedIdx, index);
      const end = Math.max(lastSelectedIdx, index);
      const rangeIds = data
        .slice(start, end + 1)
        .map((r) => r.serviceId as number)
        .filter(Boolean);
      const merged = Array.from(new Set([...selectedIds, ...rangeIds]));
      onSelectionChange(merged);
    },
    [data, selectedIds, onSelectionChange],
  );

  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'v4.border',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <CircularProgress size={32} sx={{ color: 'v4.accent' }} />
        </Box>
      )}
      <TableContainer>
        <Table size="small" aria-label="Service table">
          <ServiceTableHead
            columns={visibleColumns}
            sortModel={sortModel}
            onSort={handleSort}
            allSelected={allSelected}
            someSelected={someSelected}
            onSelectAll={handleSelectAll}
          />
          <TableBody>
            {data.map((row, idx) => {
              const id = row.serviceId as number;
              return (
                <ServiceTableRow
                  key={id ?? idx}
                  row={row}
                  columns={visibleColumns}
                  selected={selectedIds.includes(id)}
                  even={idx % 2 === 1}
                  onClick={() => toggleSelection(id)}
                  onShiftClick={() => handleShiftClick(idx)}
                  onPreview={() => onPreview(id)}
                  onAction={(action) => onRowAction(id, action)}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
